import React, { useEffect, useMemo, useState } from 'react';
import {
    Chart as ChartJS,
    BarController,
    CategoryScale,
    LinearScale,
    BarElement,
    LineElement,
    PointElement,
    Title,
    Tooltip,
    Legend
} from 'chart.js';
import { Bar, Chart, Line } from 'react-chartjs-2';
import moment from 'moment';
import MomentHelper from '../../../helpers/MomentHelper';
import { THEME_PALLETE } from '../../../Theme';
import {
    useProLanguage,
    PRO_TRANSLATIONS,
} from '../../../global/ProLanguageContext';

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarController,
    BarElement,
    LineElement,
    PointElement,
    Title,
    Tooltip,
    Legend
);

const baseOptions = {
    responsive: true,
    maintainAspectRatio: false,
    layout: {
        padding: { top: 24, right: 16, bottom: 8, left: 8 }
    },
    plugins: {
        legend: {
            position: 'top',
            labels: {
                usePointStyle: true,
                boxWidth: 10,
                font: { size: 12, weight: 500 },
                padding: 16
            }
        },
        title: {
            display: true,
            text: 'Screen Online Status',
            font: { size: 16, weight: 600 },
            padding: { bottom: 12 }
        },
        tooltip: {
            backgroundColor: '#0f172a',
            borderColor: '#334155',
            borderWidth: 1,
            titleFont: { size: 14, weight: 600 },
            bodyFont: { size: 12 },
            padding: 10,
            callbacks: {
                label: (ctx) => `${ctx.dataset.label}: ${ctx.raw}%`
            }
        }
    },
    scales: {
        x: {
            stacked: true,
            ticks: {
                maxRotation: 45,
                font: { size: 11 }
            },
            grid: {
                display: false
            }
        },
        y: {
            stacked: true,
            max: 100,
            ticks: {
                callback: (v) => `${v}%`,
                font: { size: 11 }
            },
            grid: {
                color: '#e2e8f0',
                drawBorder: false
            }
        }
    }
};

export function HealthScreenChart({ data, mode = "day" }) {
    const { lang } = useProLanguage();
    const t = (PRO_TRANSLATIONS[lang] || PRO_TRANSLATIONS.en).analytics;
    const [labels, setLabels] = useState([]);
    const [dataSet, setDataSet] = useState([]);
    const [playbackDataSet, setPlaybackDataSet] = useState([]);
    const [modeTitle, setModeTitle] = useState('');
    const [playbackModeTitle, setPlaybackModeTitle] = useState('');

    useEffect(() => {
        let newLabels = [];
        let onlineData = [];
        let offlineData = [];
        let noData = [];
        let title = "";
        let playbackTitle = "";
        let active = [];
        let idle = [];

        var connectivityBarThickness = 30;
        var playbackBarThickness = 10;

        switch (mode) {
            case "day": {
                connectivityBarThickness = 50;
                playbackBarThickness = 50;
                const items = data?.DataByDate?.Data || [];
                newLabels = items.map(item => MomentHelper.shortenTimeRangeFormat(item.Hour));
                onlineData = items.map(item => item.OnlineDuration ? 100 * item.OnlineDuration / 3600 : 0);
                offlineData = items.map(item => item.OfflineDuration ? 100 * item.OfflineDuration / 3600 : 0);
                noData = items.map(item =>   100 * (3600 - item.OfflineDuration - item.OnlineDuration) / 3600);
               
                title = t.chartConnectivityDay || "Connectivity - Today";
                playbackTitle = t.chartPlaybackDay || "Playback - Today";
                break;
            }
            case "week": {
                connectivityBarThickness = 50;
                playbackBarThickness = 50;
                const items = data?.DataByWeek || [];
                newLabels = items.map(item => moment(item.LogDate).format('ddd D MMM'));
                onlineData = items.map(item => item.ActivePercentage || 0);
                offlineData = items.map(item => item.InactivePercentage || 0);
                noData = items.map(item => item.NotDataPercentage || 0);
                title = t.chartConnectivityWeek || "Connectivity - Past 7 full days (excluding today)";
                playbackTitle = t.chartPlaybackWeek || "Playback - Past 7 full days (excluding today) - subject to a 48-hour processing delay";
                active = items.map(item => item.MediaLogActive || 0);
                idle = items.map(item => (100 - item.MediaLogActive) || 0);
                break;
            }
            case "month": {
                connectivityBarThickness = 20;
                playbackBarThickness = 20;
                const items = data?.DataByMonth || [];
                newLabels = items.map(item => moment(item.LogDate).format('MMM D'));
                onlineData = items.map(item => item.ActivePercentage || 0);
                offlineData = items.map(item => item.InactivePercentage || 0);
                noData = items.map(item => item.NotDataPercentage || 0);
                title = t.chartConnectivityMonth || "Connectivity - Past 30 full days (excluding today)";
                playbackTitle = t.chartPlaybackMonth || "Playback - Past 30 full days (excluding today) - subject to a 48-hour processing delay";
                active = items.map(item => item.MediaLogActive || 0);
                idle = items.map(item => (100 - item.MediaLogActive) || 0);
                break;
            }
        }

        setLabels(newLabels);
        setModeTitle(title);
        setPlaybackModeTitle(playbackTitle);
        setDataSet([
            {
                label: t.chartOnline || 'Online',
                data: onlineData,
                backgroundColor: '#21c45d', // green-600
                borderRadius: 0,
                barPercentage: 0.9,
                type: "bar",
                order: 1,
                stack: "connectivity",
                barThickness: connectivityBarThickness,
            },
            {
                label: t.chartOffline || 'Offline',
                data: offlineData,
                backgroundColor: '#ef4343', // red-600
                borderRadius: 0,
                barPercentage: 0.9,
                type: "bar",
                order: 1,
                stack: "connectivity",
                barThickness: connectivityBarThickness,
            },
            {
                label: t.chartNoData || 'No Data',
                data: noData,
                backgroundColor: '#ced4da', // gray-400
                borderRadius: 0,
                barPercentage: 0.9,
                type: "bar",
                order: 1,
                stack: "connectivity",
                barThickness: connectivityBarThickness,
            },
            // {
            //     label: 'Active',
            //     data: active,
            //     backgroundColor: THEME_PALLETE.blue,
            //     borderColor: THEME_PALLETE.blue,
            //     fill: false,
            //     type: 'line',
            //     tension: 0.4,
            //     order: 20, // higher order — renders on top
            //     pointBackgroundColor: THEME_PALLETE.blue,
            //     pointRadius: 3,
            //     borderWidth: 2,
            //     clip: false,
            // },
            
        ]);
        setPlaybackDataSet([
            
            {
                label: t.chartPlayerActive || 'Player Active',
                data: active,
                backgroundColor: THEME_PALLETE.blue,
                borderRadius: 0,
                barPercentage: 0.9,
                type: "bar",
                stack: "",
                order: 1,
                stack: "playback",
                barThickness: playbackBarThickness,
            }
            , {
                label: t.chartNoData || 'No Data',
                data: idle,
                backgroundColor: "#ced4da",
                borderRadius: 0,
                barPercentage: 0.9,
                type: "bar",
                stack: "",
                order: 1,
                stack: "playback",
                barThickness: playbackBarThickness,
            }
        ]);
    }, [data, mode, lang]);

    const chartOptions = useMemo(() => ({
        ...baseOptions,
        plugins: {
            ...baseOptions.plugins,
            title: {
                ...baseOptions.plugins.title,
                text: modeTitle
            }
        }
    }), [modeTitle]);

    const playbackChartOptions = useMemo(() => ({
        ...baseOptions,
        plugins: {
            ...baseOptions.plugins,
            title: {
                ...baseOptions.plugins.title,
                text: playbackModeTitle
            }
        }
    }), [playbackModeTitle]);

    return (
        <>
        <div style={{ height: 300 }}>
            <Chart
                type="bar"
                options={chartOptions}
                data={{ labels, datasets: dataSet }}
            />
        </div>
        <div style={{ height: 300 , position:"relative"}}>
            <Chart
                type="bar"
                options={playbackChartOptions}
                data={{ labels, datasets: playbackDataSet }}
            />
            {(mode == "day") && <div style={{position:"absolute", backgroundColor:"rgba(250,250,250,0.5)", left:0, top:0, width:"100%", height:"100%",  borderRadius:"12px", display:"flex", justifyContent:"center", alignItems:"center",  fontSize:"1rem", zIndex:1000, backdropFilter:"blur(3px)", color: '#64748b', fontWeight: 500}}>
                {t.chartProcessingMessage || "Data is being processed, please allow up to 48 hours for playback data to be available."}
            </div>}
        </div>
        </>
    );
}
